/**
 * /website — the stable front door to the customer's Website Studio.
 *
 * One team has one project, so the sidebar can't know the project id up
 * front; this route resolves it and forwards. Superadmins (many projects)
 * are sent to the project list instead.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { apiHelpers } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/stores/auth';

export default function WebsiteRedirectPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const projectsQ = useQuery({
    queryKey: qk.myProject(),
    queryFn: async () => {
      const res = await apiHelpers.getProjects({ limit: 1 });
      return (res.data?.data?.[0] ?? null) as { id: string } | null;
    },
    enabled: !isSuperAdmin,
  });

  useEffect(() => {
    if (isSuperAdmin) {
      navigate('/projects', { replace: true });
      return;
    }
    if (projectsQ.isPending) return;
    navigate(projectsQ.data ? `/projects/${projectsQ.data.id}/website` : '/welcome', {
      replace: true,
    });
  }, [isSuperAdmin, projectsQ.isPending, projectsQ.data, navigate]);

  return (
    <div className="flex h-64 items-center justify-center">
      <Loader2 className="text-primary h-6 w-6 animate-spin" />
    </div>
  );
}
